import React, { useState, useEffect } from 'react';
import {
    GitCommit, RotateCcw, FileText, Loader2, CheckCircle2,
    AlertCircle, Clock,
} from 'lucide-react';
import { toast, confirm as confirmModal } from '../ui/Toast';
import {
    listConsolidations, rollbackConsolidation,
} from '../../services/consolidationApi';

const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatCost = (v) => {
    if (v == null) return '—';
    const n = parseFloat(v);
    if (!Number.isFinite(n)) return '—';
    if (n < 0.01) return `$${n.toFixed(4)}`;
    return `$${n.toFixed(3)}`;
};

const STATUS_BADGE = {
    active: { label: 'Actif', color: '#22c55e', bg: 'rgba(34,197,94,0.12)', icon: CheckCircle2 },
    rolled_back: { label: 'Rollback', color: '#94a3b8', bg: 'rgba(148,163,184,0.12)', icon: RotateCcw },
    expired: { label: 'Délai dépassé', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', icon: Clock },
};

const ConsolidationHistory = ({ projectId, onChanged }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [rollingBackId, setRollingBackId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const data = await listConsolidations(projectId);
            setItems(data);
        } catch (err) {
            toast.error(err.message || 'Erreur chargement historique');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [projectId]);

    const handleRollback = async (item) => {
        const ok = await confirmModal(
            `Rollback de la consolidation du ${formatDate(item.created_at)} ? ${item.messages_consolidated} message(s) seront restaurés.`,
            { title: 'Rollback consolidation', confirmText: 'Rollback', danger: true }
        );
        if (!ok) return;
        setRollingBackId(item.id);
        try {
            const result = await rollbackConsolidation(projectId, item.id);
            toast.success(`Consolidation rollback — ${result.messages_restored} message(s) restauré(s).`);
            await load();
            onChanged?.();
        } catch (err) {
            toast.error(err.message || 'Erreur rollback');
        } finally {
            setRollingBackId(null);
        }
    };

    if (loading) {
        return (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>
                <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                <div style={{ marginTop: 8, fontSize: 12 }}>Chargement de l'historique…</div>
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div style={{
                padding: 24, textAlign: 'center',
                border: '1px dashed var(--border-color)', borderRadius: 10,
                color: 'var(--text-secondary)', fontSize: 13,
            }}>
                <GitCommit size={24} style={{ color: 'var(--text-muted)', marginBottom: 8 }} />
                <div>Aucune consolidation sur ce projet.</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>
                    Depuis l'onglet <strong>Chat</strong>, clique sur le bouton « Consolider » dans l'en-tête d'une conversation.
                </div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map(item => {
                const badge = STATUS_BADGE[item.status] || STATUS_BADGE.active;
                const BadgeIcon = badge.icon;
                const canRollback = item.status === 'active';

                return (
                    <div key={item.id} style={{
                        padding: '14px 16px', borderRadius: 12,
                        background: 'var(--card-bg)',
                        border: '1px solid var(--border-color)',
                        display: 'flex', flexDirection: 'column', gap: 8,
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                                <div style={{
                                    width: 32, height: 32, borderRadius: 8,
                                    background: 'rgba(59,130,246,0.12)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                    <GitCommit size={16} style={{ color: '#3b82f6' }} />
                                </div>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {item.session_title || `Session #${item.session_ref}`}
                                    </div>
                                    <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                                        {formatDate(item.created_at)} · par {item.user_name}
                                    </div>
                                </div>
                            </div>
                            <div style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                padding: '3px 10px', borderRadius: 8,
                                background: badge.bg, color: badge.color,
                                fontSize: 11, fontWeight: 600,
                            }}>
                                <BadgeIcon size={12} /> {badge.label}
                            </div>
                        </div>

                        <div style={{
                            display: 'flex', flexWrap: 'wrap', gap: 14,
                            fontSize: 12, color: 'var(--text-secondary)',
                            paddingLeft: 42,
                        }}>
                            <span>{item.messages_consolidated} message(s)</span>
                            <span>·</span>
                            <span>{item.model_used}</span>
                            <span>·</span>
                            <span>Coût : <strong style={{ color: 'var(--text-primary)' }}>{formatCost(item.cost_usd)}</strong></span>
                            {item.needs_revalidation_count > 0 && (
                                <>
                                    <span>·</span>
                                    <span style={{ color: '#f59e0b' }}>
                                        <AlertCircle size={11} style={{ verticalAlign: 'middle', marginRight: 2 }} />
                                        {item.needs_revalidation_count} insight(s) à revalider
                                    </span>
                                </>
                            )}
                            {item.status === 'active' && (
                                <>
                                    <span>·</span>
                                    <span>Rollback possible jusqu'au {formatDate(item.rollback_until)}</span>
                                </>
                            )}
                            {item.status === 'rolled_back' && item.rolled_back_at && (
                                <>
                                    <span>·</span>
                                    <span>Rollback le {formatDate(item.rolled_back_at)}</span>
                                </>
                            )}
                        </div>

                        <div style={{ display: 'flex', gap: 8, paddingLeft: 42, flexWrap: 'wrap' }}>
                            {item.synthesis_filename && !item.synthesis_deleted && (
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                    fontSize: 11.5, color: 'var(--text-secondary)',
                                }}>
                                    <FileText size={12} /> {item.synthesis_filename}
                                </span>
                            )}
                            {canRollback && (
                                <button
                                    onClick={() => handleRollback(item)}
                                    disabled={rollingBackId === item.id}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 4,
                                        padding: '4px 10px', borderRadius: 8, fontSize: 11.5,
                                        background: 'rgba(239,68,68,0.08)',
                                        border: '1px solid rgba(239,68,68,0.25)',
                                        color: '#ef4444', cursor: 'pointer',
                                        opacity: rollingBackId === item.id ? 0.5 : 1,
                                    }}
                                >
                                    {rollingBackId === item.id
                                        ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                                        : <RotateCcw size={12} />}
                                    Rollback
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default ConsolidationHistory;
